import React, { useMemo } from 'react';
import { Establishment } from '../../../../hooks/use-establishments';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import Table from './Table';
import moment from '../../../../moment';
import Code95Points from '../../../StudentRisk/Code95Points';
import { code95Filter } from '../../Filters/Code95Filter';
import { isBeforeFilter } from '../../Filters/IsBeforeFilter';
import { booleanFilter } from '../../Filters/BooleanFilter';

type Props = {
	students: Code95DataEntry[];
	establishmentData: Establishment[];
};

export type Code95DataEntry = {
	id: number;
	currentCycle: {
		practiceHours: string | null;
		theoryHours: string | null;
		endDate: string | null;
	} | null;
	lastNameAndInsertion: string;
	firstName: string;
	initials: string;
	dateOfBirth: string;
	establishment: {
		id: number;
		name: string;
	};
};

const columnHelper = createColumnHelper<Code95DataEntry>();

const Code95Overview = ({ students, establishmentData }: Props) => {
	const { t } = useTranslation();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor('lastNameAndInsertion', {
					header: t('employees.lastName'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('firstName', {
					header: t('employees.firstName'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('initials', {
					header: t('employees.initials'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('dateOfBirth', {
					header: t('employees.dateOfBirth'),
					enableColumnFilter: false,
				}),
				columnHelper.accessor('currentCycle.theoryHours', {
					header: t('code95.code95'),
					cell: (info) => {
						if (!info.row.original.currentCycle?.endDate) {
							return t('dashboard.no_current_cycle');
						}

						return <Code95Points cycle={info.row.original.currentCycle} />;
					},
					meta: {
						filterVariant: 'code95Filter',
					},
					filterFn: code95Filter,
				}),
				columnHelper.accessor('currentCycle.practiceHours', {
					header: t('employees.practical'),
					cell: (info) =>
						parseInt(info.cell.getValue() || '0', 10) > 0 ? (
							<div className="mt-1">
								<span className="glyphicons glyphicons-square-empty-check text-success" />
							</div>
						) : (
							<div className="mt-1">
								<span className="glyphicons glyphicons-square-empty-remove text-danger" />
							</div>
						),
					filterFn: booleanFilter,
					meta: {
						filterVariant: 'boolean',
					},
				}),
				columnHelper.accessor('currentCycle.endDate', {
					header: t('code95.code95ExpirationDate'),
					cell: (info) => (info.cell.getValue() ? moment(info.cell.getValue()).format('L') : null),
					meta: {
						filterVariant: 'expirationDate',
						filterLabel: t('code95.code95IsBefore'),
					},
					filterFn: isBeforeFilter,
				}),
			] as Array<AccessorKeyColumnDef<Code95DataEntry, unknown>>,
		[t],
	);

	return <Table data={students} columns={columns} establishmentData={establishmentData} />;
};

export default Code95Overview;
